
import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator';
import SatellitalEvolutionChart from '../../../features/item_with_sinisters/components/satellital/SatellitalEvolutionChart.vue';
import SinisterMapLayers from '../../../features/item_with_sinisters/components/sinisters/SinisterMapLayers.vue';
import SinisterInspection from '../../../features/item_with_sinisters/components/sinisters/SinisterInspection.vue';
import SummaryItems from '../../../features/item_with_sinisters/components/sinisters/summary/SummaryItems.vue';
import SinisterNotes from '../../../features/item_with_sinisters/components/sinisters/SinisterNotes.vue';
import { formatDate, parseFloatNumber } from '../../../features/core/utils/date-handlers';
import { SatellitalLayersInfo } from '../../../features/typescript/satellital/layers';
import { ClaimDetailResponse, UpdateClaimPolygonResponse } from '../../../features/core/api/ClaimsApi';
import ItemIndicators from '../../../features/item/components/ItemIndicators.vue';
import { FeatureCollection } from '../../../features/typescript/MapShapes';

@Component({
    components: {
        ItemIndicators,
        SinisterMapLayers,
        SinisterInspection,
        SummaryItems,
        SinisterNotes,
        SatellitalEvolutionChart,
    }
})
export default class ClaimDetailView extends Vue {
    @Prop({ required: true }) claimInfo!: ClaimDetailResponse;
    @Prop({ default: () => null }) itemPolygon: any;

    localClaimInfo: ClaimDetailResponse | null = null;
    localItemPolygon: FeatureCollection | null = null;

    satellitalLayersInfo: SatellitalLayersInfo = {
        zonified: {
            date: null,
            png_url: null,
            total_area: null,
            bar_url: '',
            zones: [],
            bounds: null
        },
        ndvi: {
            initial_date: null,
            final_date: null,
            png_url: null,
            bar_url: '',
            total_area: null,
            zones: [],
            bounds: null
        }
    }

    mounted() {
        // Create a local copy of claimInfo
        this.localClaimInfo = { ...this.claimInfo };
        this.localItemPolygon = this.itemPolygon;
    }

    get activeClaimInfo() {
        return this.localClaimInfo || this.claimInfo;
    }

    get claimIndicators() {
        const eventTypeName = this.getEventTypeName(this.activeClaimInfo.event_type);
        const cropTypeName = this.getCropTypeName(this.activeClaimInfo.crop_type);
        
        return {
            creation_date: this.parseDate(this.activeClaimInfo.creation_date),
            claim_id: this.activeClaimInfo.claim_id,
            event_type: eventTypeName,
            event_date: this.parseDate(this.activeClaimInfo.event_date),
            crop_type: cropTypeName,
            declared_area: parseFloatNumber(this.activeClaimInfo.declared_area, 1),
            area: this.activeClaimInfo.area
        }
    }

    get sinisterWithAppraisers() {
        // Transform appraisers string array to Appraiser objects
        const appraisers_list = (this.activeClaimInfo.appraisers || []).map(email => ({ email }));
        return {
            ...this.activeClaimInfo,
            appraisers_list
        };
    }

    onPolygonUpdated(payload: Pick<UpdateClaimPolygonResponse, 'ranch_area' | 'polygon' | 'using_original_polygon'>) {
        if (this.localClaimInfo) {
            this.localClaimInfo = {
                ...this.localClaimInfo,
                area: payload.ranch_area,
                ...(payload.polygon ? { polygon: payload.polygon } : {}),
                ...(payload.using_original_polygon !== undefined ? { using_original_polygon: payload.using_original_polygon } : {})
            };
        }
        if (payload.polygon) {
            this.localItemPolygon = payload.polygon;
        }
    }

    get eventTypeTranslator() {
        return this.$store.getters['properties/getEventTypeName'];
    }

    get cropTypeTranslator() {
        return this.$store.getters['properties/getCropTypeName'];
    }

    getEventTypeName(eventType: number): string {
        if (this.eventTypeTranslator) {
            return this.eventTypeTranslator(eventType);
        }
        return eventType && eventType.toString() || '';
    }

    getCropTypeName(cropType: number): string {
        if (this.cropTypeTranslator) {
            return this.cropTypeTranslator(cropType);
        }
        return cropType && cropType.toString() || '';
    }
    
    parseDate(value: string){
       return formatDate(value, false, true)
    }

    seeNotesInMap(notes) {
        const mapComponent = this.$refs.map as any;
        if (mapComponent && typeof mapComponent.seeNotesInMap === 'function') {
            mapComponent.seeNotesInMap(notes);
        }
    }

    onClickedChartPoint(pointIndex: number) {
        const mapComponent = this.$refs.map as any;
        if (mapComponent && typeof mapComponent.clickedSatelliteImage === 'function') {
            mapComponent.clickedSatelliteImage(pointIndex);
        }
    }

    updateCarouselIndex(idx: number) {
        const chartComponent = this.$refs.chart as any;
        if (chartComponent && typeof chartComponent.updateCarouselIndex === 'function') {
            chartComponent.updateCarouselIndex(idx);
        }
    }

    updateSelectedImageInChart(img: any) {
        const chartComponent = this.$refs.chart as any;
        if (chartComponent && typeof chartComponent.updateSelectedImage === 'function') {
            chartComponent.updateSelectedImage(img);
        }
    }

    updateSelectedBandInChart(band: any) {
        const chartComponent = this.$refs.chart as any;
        if (chartComponent && typeof chartComponent.updateSelectedBand === 'function') {
            chartComponent.updateSelectedBand(band);
        }
    }

    updateLayersInfoInChilds(newLayers: SatellitalLayersInfo) {
        const sumZComponent = this.$refs.sumZ as any;
        const sumNComponent = this.$refs.sumN as any;
        const chartComponent = this.$refs.chart as any;

        if (sumZComponent && typeof sumZComponent.updateLayersInfo === 'function') {
            sumZComponent.updateLayersInfo(newLayers.zonified);
        }
        if (sumNComponent && typeof sumNComponent.updateLayersInfo === 'function') {
            sumNComponent.updateLayersInfo(newLayers.ndvi);
        }
        if (chartComponent && typeof chartComponent.updateSatellitalLayersInfo === 'function') {
            chartComponent.updateSatellitalLayersInfo(newLayers);
        }
    }

    updateLoadingZonification(v: boolean) {
        const sumZComponent = this.$refs.sumZ as any;
        if (sumZComponent && typeof sumZComponent.updateLoadingIndicator === 'function') {
            sumZComponent.updateLoadingIndicator(v);
        }
    }

    updateLoadingNdviDiff(v: boolean) {
        const sumNComponent = this.$refs.sumN as any;
        if (sumNComponent && typeof sumNComponent.updateLoadingIndicator === 'function') {
            sumNComponent.updateLoadingIndicator(v);
        }
    }

    updateLoadingMapInChilds(v: boolean) {
        const chartComponent = this.$refs.chart as any;
        if (chartComponent && typeof chartComponent.updateIsLoadingLayers === 'function') {
            chartComponent.updateIsLoadingLayers(v);
        }
        this.updateLoadingZonification(v);
        this.updateLoadingNdviDiff(v);
    }

    addRgbNdviToChart(values: any) {
        const chartComponent = this.$refs.chart as any;
        if (chartComponent && typeof chartComponent.setRgbAndNdviValues === 'function') {
            chartComponent.setRgbAndNdviValues(values);
        }
    }
}
